import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import { getTenant } from '../utils/tenant'
import { CurrentUserDto } from '../types/django/user' 
import { 
  authService, 
  CompleteActivationResponseDto, 
  MfaRequiredResponseDto 
} from '../services/authService'
import { DjangoTokenPairDto } from '../types/django'

interface AuthContextType {
  user: CurrentUserDto | null 
  isLoading: boolean
  isAuthenticated: boolean
  tenant: string
  permissions: Record<string, boolean>
  login: (email: string, password: string, rememberMe?: boolean) => Promise<DjangoTokenPairDto | MfaRequiredResponseDto>
  verifyOtp: (mfaToken: string, otpCode: string) => Promise<void>
  completeActivation: (payload: { email: string; tempToken: string | null; newPassword: string }) => Promise<CompleteActivationResponseDto>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUserDto | null>(null) 
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()

  const tenant = useMemo(() => getTenant(), [])
  const isAuthenticated = !!user

  const permissions = useMemo(() => {
    if (!user) return {}
    // @ts-ignore - En attendant que le type CurrentUserDto intègre formellement role_group
    return user.permissions || user.role_group?.permissions || {}
  }, [user])

  const completeActivation = useCallback(
    async (payload: { email: string; tempToken: string | null; newPassword: string }): Promise<CompleteActivationResponseDto> => {
      if (!payload.tempToken) {
        throw new Error('Le jeton temporaire est requis.');
      }

      queryClient.clear();

      const response = await authService.completeActivation({
        email: payload.email,
        temp_token: payload.tempToken, 
        newPassword: payload.newPassword,
      } as any); 

      return response;
    },
    [queryClient]
  );

  // VALIDATION OTP
  const verifyOtp = useCallback(async (mfaToken: string, otpCode: string) => {
    queryClient.clear()
    
    await authService.verifyOtp({
      mfa_token: mfaToken,
      otp_code: otpCode
    })
    
    const userData = await authService.fetchUser()
    setUser(userData)
  }, [queryClient])

  // LOGIN CLASSIQUE
 // AuthContext.tsx
interface AuthContextType {
  // ...
  login: (email: string, password: string, rememberMe?: boolean) => Promise<DjangoTokenPairDto | MfaRequiredResponseDto>
}

// Et dans la fonction :
const login = useCallback(async (email: string, password: string, rememberMe: boolean = true): Promise<DjangoTokenPairDto | MfaRequiredResponseDto> => {
  queryClient.clear()
  const response = await authService.login({ email, password }, rememberMe) // <--- Transmettre rememberMe ici

  if ('access' in response) {
    const userData = await authService.fetchUser()
    setUser(userData)
  }

  return response
}, [queryClient])

  useEffect(() => {
    let isMounted = true

    const init = async () => {
      // 1. Vérifier si l'utilisateur est sur une page publique
      const pathname = window.location.pathname.toLowerCase()
      const isPublicPage = [
        '/login',
        '/signup',
        '/activate-account',
        '/activate',
        '/reset-password',   
        '/forgot-password',
        '/provisioning', 
      ].some((path) => pathname.startsWith(path))

      // Si on est sur une page publique, réinitialiser ou ignorer la restauration automatique de session
      if (isPublicPage) {
        if (isMounted) setIsLoading(false)
        return
      }

      try {
        const cachedUser = authService.getStoredUser()
        if (cachedUser && isMounted) setUser(cachedUser)

        const token = authService.getAccessToken()
        if (!token) {
          if (isMounted) setIsLoading(false)
          return
        }

        const userData = await authService.fetchUser()
        if (isMounted) setUser(userData)

      } catch {
        try {
          const newAccess = await authService.refreshAccessToken()

          if (newAccess) {
            const userData = await authService.fetchUser()
            if (isMounted) setUser(userData)
          } else {
            authService.clearAuth()
            if (isMounted) setUser(null)
          }
        } catch {
          authService.clearAuth()
          if (isMounted) setUser(null)
        }
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    init()

    return () => {
      isMounted = false
    }
  }, [])

  const logout = useCallback(() => {
    authService.logout()
    queryClient.clear()
    setUser(null)
  }, [queryClient])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        tenant,
        permissions, 
        login,
        verifyOtp,
        completeActivation,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }

  return context
}